/**
 * Pure helpers shared by the master-passphrase screens
 * (plans/26_07_2026/master-password/CONTRACT.md §5, §6.2, §6.3).
 *
 * Everything here is a MIRROR for live feedback, never the enforcement point.
 * Rust's `vault_key::validate_passphrase` is the trust boundary — the same
 * argument `apply_passkey_provenance` makes for itself — so a screen that gets
 * one of these rules wrong produces a worse hint, not a weaker vault.
 *
 * Nothing in this module logs, stores or serialises a passphrase or a recovery
 * phrase. Values are read, compared and dropped.
 */
import { RECOVERY_WORDSET } from './vaultRecoveryWordlist';

/** §5 — the character floor. Mirrors `vault_key::MIN_PASSPHRASE_CHARS`. */
export const MIN_PASSPHRASE_CHARS = 12;

/** §5 — the byte cap. Mirrors `vault_key::MAX_PASSPHRASE_BYTES`. */
export const MAX_PASSPHRASE_BYTES = 1024;

/** Card 1.5 — the fewest distinct characters. Mirrors `vault_key::MIN_DISTINCT_CHARS`. */
export const MIN_DISTINCT_CHARS = 5;

/** A recovery phrase is always exactly this many words. */
export const RECOVERY_WORD_COUNT = 12;

/**
 * NFC, mandatory (§5, Disagreement 7). Rust normalises the same way before it
 * derives, so a passphrase typed with a composed é on one keyboard and a
 * decomposed e+◌́ on another is the same secret. Comparing `confirm` against
 * `passphrase` without this produces a mismatch the user cannot see.
 */
export function nfc(value) {
  return typeof value === 'string' ? value.normalize('NFC') : '';
}

/** UTF-8 byte length, for the 1024-byte cap. */
export function byteLength(value) {
  if (!value) return 0;
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(value).length;
  return unescape(encodeURIComponent(value)).length;
}

/**
 * `vault_key::normalize_phrase` verbatim: lowercase every whitespace-separated
 * token and join on single spaces. Capitals and extra spaces genuinely do not
 * matter, which is what the unlock screen promises.
 */
export function normalizePhrase(input) {
  return String(input || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.toLowerCase())
    .join(' ');
}

/** The normalised tokens of a typed recovery phrase. */
export function phraseWords(input) {
  const normalized = normalizePhrase(input);
  return normalized ? normalized.split(' ') : [];
}

/**
 * Typed words that are not on the BIP39 list, in first-seen order and deduped.
 *
 * Advisory only: it may never block submission (§6.3). A user whose phrase is
 * correct but whose 13th token is a stray character still gets to press Unlock.
 */
export function unknownRecoveryWords(input) {
  const seen = new Set();
  const out = [];
  for (const w of phraseWords(input)) {
    if (RECOVERY_WORDSET.has(w) || seen.has(w)) continue;
    seen.add(w);
    out.push(w);
  }
  return out;
}

/**
 * The 4-point character-class check from `SettingsModal.js:173-181`, reproduced
 * so the passphrase screens do not import from a 3k-line settings module.
 *
 * It is NOT an entropy estimate (§6.9). It exists for visual consistency with
 * the sync passphrase field; the "weak" copy deliberately talks about offline
 * guessing rather than claiming this measured guessability.
 */
export function pwStrength(pw) {
  let s = 0;
  if (pw.length >= 8) s += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s += 1;
  if (/\d/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  return Math.min(s, 4);
}

/**
 * The blocking problems with a proposed passphrase, mirrored from §5 so the
 * primary button can be disabled before a round trip.
 *
 * Returns `null` when nothing local objects — that is NOT a promise Rust will
 * accept it (the compiled blocklist lives only there), so callers must still
 * render whatever `passphrase_weak` comes back with.
 *
 * `edgeSpace` is its own case on purpose: a pasted trailing space is a classic
 * permanent lockout, and rejecting it at setup is what lets unlock never trim.
 */
export function passphraseProblem(passphrase, confirm) {
  const pass = nfc(passphrase);
  if (!pass) return 'empty';
  if ([...pass].length < MIN_PASSPHRASE_CHARS) return 'tooShort';
  if (byteLength(pass) > MAX_PASSPHRASE_BYTES) return 'tooLong';
  if (pass !== pass.trim()) return 'edgeSpace';
  if (new Set([...pass]).size < MIN_DISTINCT_CHARS) return 'tooRepetitive';
  if (confirm != null && confirm !== '' && pass !== nfc(confirm)) return 'mismatch';
  if (confirm != null && confirm === '') return 'empty';
  return null;
}

/**
 * Two distinct 1-based word positions to confirm (§6.2 step 2).
 *
 * `rng` is injectable so a test can pin the positions; production passes
 * nothing and gets `Math.random`. Deliberately not crypto-random: this is a
 * "did you actually write it down" check, not a secret.
 */
export function pickConfirmPositions(count = RECOVERY_WORD_COUNT, rng = Math.random) {
  if (count < 2) return [1, 1];
  const a = Math.floor(rng() * count) + 1;
  let b = Math.floor(rng() * (count - 1)) + 1;
  if (b >= a) b += 1;
  return a < b ? [a, b] : [b, a];
}

/**
 * Case- and whitespace-insensitive comparison for the confirm step. The words
 * are lowercase, but a phone keyboard capitalises the first letter and a user
 * copying from paper adds spaces; rejecting either would teach them their
 * correctly-written phrase is wrong.
 */
export function sameWord(typed, expected) {
  return String(typed || '').trim().toLowerCase() === String(expected || '').trim().toLowerCase();
}
